import React, { useState, useEffect, useRef } from 'react';
import { useGroup } from '../../context/GroupContext';
import { useAuth } from '../../context/AuthContext';
import { X, Search, Plus, Check, ChevronDown, Calendar } from 'lucide-react';
import groupService from '../../api/groupService';

const AddPledgeModal = ({ isOpen, onClose, onPledgeCreated, pledgeToEdit = null }) => {
  const { adminGroup } = useGroup();
  const { currentUser } = useAuth();
  const isEditMode = !!pledgeToEdit;
  
  // Form state
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedPledgeType, setSelectedPledgeType] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('unpaid');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [chargeDate, setChargeDate] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [notes, setNotes] = useState('');
  
  // Discount state
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [discountName, setDiscountName] = useState('');
  
  // Data state
  const [members, setMembers] = useState([]);
  const [pledgeTypes, setPledgeTypes] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // UI state
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [showPledgeTypeDropdown, setShowPledgeTypeDropdown] = useState(false);
  const [showNewTypeInput, setShowNewTypeInput] = useState(false);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeColor, setNewTypeColor] = useState('#3b82f6');
  
  // Refs
  const memberDropdownRef = useRef(null);
  const pledgeTypeDropdownRef = useRef(null);

  // Load members and pledge types
  useEffect(() => {
    if (isOpen && adminGroup?.id) {
      loadMembers();
      loadPledgeTypes();
      
      // If editing, populate form with existing pledge data
      if (isEditMode && pledgeToEdit) {
        // Set member (convert to array for multi-select compatibility)
        if (pledgeToEdit.native_users) {
          setSelectedMembers([pledgeToEdit.native_users]);
        }
        
        // Set pledge type
        if (pledgeToEdit.pledge_types) {
          setSelectedPledgeType(pledgeToEdit.pledge_types);
        }
        
        // Set amounts and status
        setAmount(pledgeToEdit.amount?.toString() || '');
        setPaymentStatus(pledgeToEdit.payment_status || 'unpaid');
        setDescription(pledgeToEdit.description || '');
        setNotes(pledgeToEdit.notes || '');
        
        // Set dates
        if (pledgeToEdit.due_date) {
          setDueDate(pledgeToEdit.due_date.split('T')[0]);
        }
        if (pledgeToEdit.charge_date) {
          setChargeDate(pledgeToEdit.charge_date.split('T')[0]);
        }
        
        // Set discount fields
        setDiscountPercentage(pledgeToEdit.discount_percentage?.toString() || '');
        setDiscountName(pledgeToEdit.discount_name || '');
      }
    }
  }, [isOpen, adminGroup?.id, isEditMode, pledgeToEdit]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target)) {
        setShowMemberDropdown(false);
      }
      if (pledgeTypeDropdownRef.current && !pledgeTypeDropdownRef.current.contains(event.target)) {
        setShowPledgeTypeDropdown(false);
        setShowNewTypeInput(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMembers = async () => {
    try {
      const response = await groupService.getGroupMembers(adminGroup.id);
      const membersData = response?.data?.data || response?.data || [];
      setMembers(membersData);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load members');
    }
  };

  const loadPledgeTypes = async () => {
    try {
      const response = await groupService.getPledgeTypes(adminGroup.id);
      const typesData = response?.data?.data || response?.data || [];
      setPledgeTypes(typesData);
    } catch (err) {
      console.error('Error loading pledge types:', err);
    }
  };

  const handleMemberToggle = (member) => {
    const memberId = member.user_id || member.id;
    if (selectedMembers.find(m => (m.user_id || m.id) === memberId)) {
      setSelectedMembers(selectedMembers.filter(m => (m.user_id || m.id) !== memberId));
    } else {
      setSelectedMembers([...selectedMembers, member]);
    }
  };

  const handleCreatePledgeType = async () => {
    if (!newTypeName.trim()) return;
    
    try {
      const pledgeTypeData = {
        name: newTypeName,
        color: newTypeColor,
        description: null
      };
      
      const response = await groupService.createPledgeType(adminGroup.id, pledgeTypeData);
      const newType = response?.data || response;
      
      setPledgeTypes([...pledgeTypes, newType]);
      setSelectedPledgeType(newType);
      setShowNewTypeInput(false);
      setShowPledgeTypeDropdown(false);
      setNewTypeName('');
      setNewTypeColor('#3b82f6');
    } catch (err) {
      console.error('Error creating pledge type:', err);
      setError('Failed to create pledge type');
    }
  };

  // Calculate discounted amount
  const calculateDiscountedAmount = () => {
    const baseAmount = parseFloat(amount) || 0;
    const discount = parseFloat(discountPercentage) || 0;
    if (discount > 0 && discount <= 100) {
      // Use integer math to avoid floating point precision issues
      const amountCents = Math.round(baseAmount * 100);
      const discountAmount = Math.round(amountCents * discount / 100);
      const finalAmountCents = amountCents - discountAmount;
      return finalAmountCents / 100;
    }
    return baseAmount;
  };

  const finalAmount = calculateDiscountedAmount();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedMembers.length === 0) {
      setError('Please select at least one member');
      return;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      if (isEditMode) {
        // UPDATE existing pledge
        const baseAmount = parseFloat(amount);
        const discount = parseFloat(discountPercentage) || 0;
        
        const pledgeData = {
          pledge_type_id: selectedPledgeType?.id || null,
          amount: baseAmount,
          payment_status: paymentStatus,
          description: description.trim() || null,
          due_date: dueDate || null,
          charge_date: chargeDate || null,
          discount_percentage: discount > 0 ? discount : null,
          discount_name: discount > 0 && discountName.trim() ? discountName.trim() : null,
          discounted_amount: discount > 0 ? finalAmount : null,
          notes: notes.trim() || null
        };
        
        await groupService.updatePledge(pledgeToEdit.id, pledgeData);
      } else {
        // CREATE new pledge(s) for each selected member
        const pledgePromises = selectedMembers.map(member => {
          const memberId = member.user_id || member.id;
          const baseAmount = parseFloat(amount);
          const discount = parseFloat(discountPercentage) || 0;
          
          const pledgeData = {
            group_id: adminGroup.id,
            member_id: memberId,
            pledge_type_id: selectedPledgeType?.id || null,
            amount: baseAmount,
            payment_status: paymentStatus,
            description: description.trim() || null,
            due_date: dueDate || null,
            charge_date: chargeDate || null,
            discount_percentage: discount > 0 ? discount : null,
            discount_name: discount > 0 && discountName.trim() ? discountName.trim() : null,
            discounted_amount: discount > 0 ? finalAmount : null,
            notification_sent: sendNotification,
            notes: notes.trim() || null,
            created_by: currentUser.id
          };
          
          return groupService.createPledge(pledgeData);
        });
        
        await Promise.all(pledgePromises);
      }
      
      if (onPledgeCreated) {
        onPledgeCreated();
      }
      
      onClose();
      resetForm();
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} pledge:`, err);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} pledge. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedMembers([]);
    setSelectedPledgeType(null);
    setAmount('');
    setPaymentStatus('unpaid');
    setDescription('');
    setDueDate('');
    setChargeDate('');
    setDiscountPercentage('');
    setDiscountName('');
    setSendNotification(true);
    setNotes('');
    setMemberSearchQuery('');
    setError(null);
  };

  const filteredMembers = members.filter(member => {
    const memberData = member.user || member;
    const fullName = memberData.full_name || memberData.name || `${memberData.first_name || ''} ${memberData.last_name || ''}`.trim();
    const email = memberData.email || '';
    const phone = memberData.phone_number || '';
    
    return fullName?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
           email?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
           phone?.includes(memberSearchQuery);
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-900">{isEditMode ? 'Edit Pledge' : 'Add Pledge'}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Select Members - Improved with search in main field */}
          <div className="relative" ref={memberDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Members <span className="text-red-500">*</span>
            </label>
            
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search members by name, email, or phone..."
                value={memberSearchQuery}
                onChange={(e) => {
                  setMemberSearchQuery(e.target.value);
                  setShowMemberDropdown(true);
                }}
                onFocus={() => setShowMemberDropdown(true)}
                disabled={isEditMode}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {/* Dropdown */}
            {showMemberDropdown && !isEditMode && (
              <div className="absolute top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                {filteredMembers.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No members found
                  </div>
                ) : (
                  <div className="p-2">
                    {filteredMembers.map(member => {
                      const memberData = member.user || member;
                      const memberId = member.user_id || member.id;
                      const fullName = memberData.full_name || memberData.name || `${memberData.first_name || ''} ${memberData.last_name || ''}`.trim();
                      const contact = memberData.email || memberData.phone_number || '';
                      const isSelected = selectedMembers.find(m => (m.user_id || m.id) === memberId);
                      
                      return (
                        <button
                          key={memberId}
                          type="button"
                          onClick={() => handleMemberToggle(member)}
                          className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <div className="flex flex-col items-start">
                            <span className="font-medium text-gray-900">
                              {fullName || 'Unknown Member'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {contact || 'No contact info'}
                            </span>
                          </div>
                          {isSelected && (
                            <div className="w-5 h-5 bg-blue-500 rounded flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Selected Members Pills */}
            {selectedMembers.length > 0 && (
              <div className="mt-2">
                <div className="flex flex-wrap gap-2">
                  {selectedMembers.slice(0, 8).map(member => {
                    const memberData = member.user || member;
                    const memberId = member.user_id || member.id;
                    const fullName = memberData.full_name || memberData.name || `${memberData.first_name || ''} ${memberData.last_name || ''}`.trim();
                    
                    return (
                      <span
                        key={memberId}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                      >
                        {fullName || 'Unknown Member'}
                        {!isEditMode && (
                          <button
                            type="button"
                            onClick={() => handleMemberToggle(member)}
                            className="hover:bg-blue-200 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    );
                  })}
                  {selectedMembers.length > 8 && (
                    <button
                      type="button"
                      onClick={() => setShowAllMembers(true)}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
                    >
                      +{selectedMembers.length - 8} more...
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Pledge Type - Improved dropdown with inline create */}
          <div className="relative" ref={pledgeTypeDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pledge Type <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            
            <button
              type="button"
              onClick={() => setShowPledgeTypeDropdown(!showPledgeTypeDropdown)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-left flex items-center justify-between hover:border-gray-400 transition-colors"
            >
              <span className={selectedPledgeType ? 'text-gray-900' : 'text-gray-500'}>
                {selectedPledgeType ? selectedPledgeType.name : 'Select pledge type (General)'}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {/* Dropdown */}
            {showPledgeTypeDropdown && (
              <div className="absolute top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                <div className="p-2">
                  {/* General/None option */}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPledgeType(null);
                      setShowPledgeTypeDropdown(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between"
                  >
                    <span className="font-medium text-gray-900">General</span>
                    {!selectedPledgeType && (
                      <Check className="w-4 h-4 text-blue-500" />
                    )}
                  </button>
                  
                  {/* Existing types */}
                  {pledgeTypes.map(type => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => {
                        setSelectedPledgeType(type);
                        setShowPledgeTypeDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: type.color }}
                        />
                        <span className="font-medium text-gray-900">{type.name}</span>
                      </div>
                      {selectedPledgeType?.id === type.id && (
                        <Check className="w-4 h-4 text-blue-500" />
                      )}
                    </button>
                  ))}
                  
                  {/* Create new type inline */}
                  {!showNewTypeInput ? (
                    <button
                      type="button"
                      onClick={() => setShowNewTypeInput(true)}
                      className="w-full px-3 py-2 text-left border-t border-gray-200 mt-2 pt-2 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2 text-blue-600"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="font-medium">Create New Type</span>
                    </button>
                  ) : (
                    <div className="border-t border-gray-200 mt-2 pt-2 space-y-2">
                      <input
                        type="text"
                        placeholder="Type name..."
                        value={newTypeName}
                        onChange={(e) => setNewTypeName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        autoFocus
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={newTypeColor}
                          onChange={(e) => setNewTypeColor(e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <button
                          type="button"
                          onClick={handleCreatePledgeType}
                          disabled={!newTypeName.trim()}
                          className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
                        >
                          Create
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewTypeInput(false);
                            setNewTypeName('');
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Amount & Discount Section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  step="any"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            {/* Discount Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount % <span className="text-gray-400 text-xs">(optional)</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="any"
                    min="0"
                    max="100"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(e.target.value)}
                    className="w-full pl-4 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Name
                </label>
                <input
                  type="text"
                  value={discountName}
                  onChange={(e) => setDiscountName(e.target.value)}
                  placeholder="e.g., Early Bird"
                  disabled={!discountPercentage || parseFloat(discountPercentage) <= 0}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {/* Show final amount if discount applied */}
            {discountPercentage && parseFloat(discountPercentage) > 0 && amount && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Original Amount:</span>
                  <span className="text-gray-900">${parseFloat(amount).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-gray-600">
                    Discount ({discountPercentage}%{discountName ? ` - ${discountName}` : ''}):
                  </span>
                  <span className="text-red-600">-${(parseFloat(amount) - finalAmount).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between font-semibold mt-2 pt-2 border-t border-blue-300">
                  <span className="text-gray-900">Final Amount:</span>
                  <span className="text-blue-600 text-lg">${finalAmount.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Payment Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Status <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'unpaid', label: 'Unpaid', color: 'bg-red-100 text-red-700 border-red-200' },
                { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-700 border-green-200' }
              ].map(status => (
                <button
                  key={status.value}
                  type="button"
                  onClick={() => setPaymentStatus(status.value)}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    paymentStatus === status.value
                      ? status.color + ' border-current'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dates Section */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Charge Date <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="date"
                value={chargeDate}
                onChange={(e) => setChargeDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">When to charge this pledge</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Due Date <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Payment deadline</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional description or purpose of the pledge..."
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Internal Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Private notes (not visible to members)..."
            />
          </div>

          {/* Send Notification Toggle - Only show when creating a new pledge with unpaid status */}
          {!isEditMode && paymentStatus === 'unpaid' && (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Send Notification</p>
              <p className="text-sm text-gray-500">Notify members about this pledge via push notification</p>
            </div>
            <button
              type="button"
              onClick={() => setSendNotification(!sendNotification)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                sendNotification ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  sendNotification ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Pledge' : 'Create Pledge')}
            </button>
          </div>
        </form>
      </div>

      {/* View All Members Modal */}
      {showAllMembers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Selected Members ({selectedMembers.length})
              </h3>
              <button
                onClick={() => setShowAllMembers(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <div className="space-y-2">
                {selectedMembers.map(member => {
                  const memberData = member.user || member;
                  const memberId = member.user_id || member.id;
                  const fullName = memberData.full_name || memberData.name || `${memberData.first_name || ''} ${memberData.last_name || ''}`.trim();
                  const contact = memberData.email || memberData.phone_number || '';
                  
                  return (
                    <div
                      key={memberId}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {fullName || 'Unknown Member'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {contact || 'No contact info'}
                        </span>
                      </div>
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={() => {
                            handleMemberToggle(member);
                            if (selectedMembers.length === 1) {
                              setShowAllMembers(false);
                            }
                          }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => setShowAllMembers(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPledgeModal;
